import router from '@/routers'
import Vue from 'vue'

import simulator from '@/mixins/calculations/simulator'
import salesTable from '@/mixins/calculations/salesTable'
import helpers from '@/mixins/helpers'

import { 
    deal,
    product,
    simulation as simulationService,
    funnel
} from '@/services/axios'

import { storage } from '@/services/storage'

const assert = require('assert')
const moment = require('moment')
require('moment/locale/pt-br')

const seriesDefaultData = () => ({
    id: 'random-' + Math.floor((Math.random() * Date.now()) + 1),
    type_installment: 'act',
    quantity: 1,
    percent: 0,
    first_value: 0,
    last_value: 0,
    first_date: null,
    first_date_default: null,
    last_date: null,
    fixed_value: 0  ,
    interest_rate: 'without',
    installments: [],
    consider: '',
    type_readjust: ''
})

const generateSeries = (item) => {
    /**
     * Calcula a data de vencimento da primeira parcela de acordo com a quantidade de dias
     * definida na tabela de vendas
     */
    let dueDate = Date.now()
    if (item.due_date) dueDate += Math.ceil(item.due_date * 1000 * 60 * 60 * 24)
    let date = moment(new Date(dueDate)).format('YYYY-MM-DD')
    return {
        id: 'random-' + Math.floor((Math.random() * Date.now()) + 1),
        type_installment: item.parcel_type,
        quantity: parseInt(item.quota),
        percent: parseFloat(item.percent),
        first_value: parseFloat(item.pmt),
        last_value: parseFloat(item.pmt),
        first_date: date,
        first_date_default: date,
        last_date: null,
        interest_rate: item.interest,
        installments: [],
        consider: '',
        type_readjust: item.type_readjust ?? ''
    }
}

/**
 * Calcula o percentual referente a determinados tipos de parcelas a serem validadas
 * de acordo com as regras de simulação
 */
const getSpecificPercent = (types, series, ruleDate = null, unityValue = null) => {
    let percent = 0

    for (const parcel of series) {
        /** Verifica se há restrição de tipos das parcelas/séries, ou se o */
        if ((types.length && !types.includes(parcel.type_installment)) ||
            parcel.consider === 'unity-discount' ||
            parcel.type_installment === 'fixed_value') continue

        if (ruleDate) {
            if (parcel.consider === 'payment-proposal' &&
                moment(parcel.first_date, 'YYYY-MM-DD').isSameOrBefore(ruleDate)) {
                percent += parseFloat(parcel.percent)

            } else {
                /** Filtra as parcelas de uma série que estão dentor do limite definido pela regra de simulação */
                let installments = parcel.installments.filter(item => moment(item.duedate, 'YYYY-MM-DD').isSameOrBefore(ruleDate))

                /**
                 * Caso hajam parcelas dentro do limite definido pela data da regra de simulação, calcula-se o percentual
                 * referente a essas parcelas.
                 */
                if (!installments.length) continue

                /**
                 * Calcula-se o percentual de captação referente as parcelas que estão dentro do intervalo
                 * de tempo definido.
                 */
                let total = installments.map(installment => parseFloat(installment.valueInstallments))
                    .reduce((a, b) => a + b, 0)
                percent += (total / parseFloat(unityValue)) * 100
            }

        } else {
            percent += parseFloat(parcel.percent)
        }
    }

    return percent.toFixed(2)
}

const extendableTypes = [
    'unique',
    'monthly',
    'bimonthly',
    'quarterly',
    'four_monthly',
    'semester',
    'yearly',
    'keys',
    'signal',
    'intermediary',
    'assets_rights'
]

const simulationData = () => ({
	id: null,
	customer: null,
	deal: null,
	default: 1,
	observation: '',
	owner: null,
	product: {
		availability: {}
	},
	salesTable: {
		id: null,
        default_config: '',
		discount: 0,
		duedates: [],
		monthly_rate: null,
		sales_table_flow: [],
		sales_commission_rules: [],
		sales_simulator_rules: [],
		type: 'price',
		observations: null,
	},
	salesTableFlow: null,
	series: [],
	unity: {},
	discount_saved: 0,
	unity_cost: 0,
    custom_unity_cost:0,
	hasProductInLaunchday: false

})

const defaultSimulationData = () => ({
    baseCost: 0,
    futureValue: 0,
    presentValue: 0,
    series: []
})

export const simulation = {
    namespaced: true,

    state: {
        data: { ...simulationData() },
        default: { ...defaultSimulationData() },
        extendableTypes: [...extendableTypes],
        custom_unity_cost: 0,
        stageAllowFill: null
    },

    getters: {
        getCustomValue(state){
            return state.data.custom_unity_cost;
        },
        /**
         * Retorna o ID de uma simulação indicando se esta já foi salva no banco
         */
        getSimulationId(state) {
            return state.data.id
        },

        /**
         * Retorna o produto selecionado pelo usuário
         */
        getProduct(state) {
            return state.data.product
        },
        /**
         * Retorna a unidade selecionada pelo usuário
         */
        getUnity(state) {
            return state.data.unity
        },

        /**
         * Retorna o preço da unidade selecionada pelo usuário
         */
        getUnityCost(state) {
            return state.data.unity_cost
        },

        /**
         * Retorna todas as séries/parcelas para comparação com as series da simulação 
         */
        getStaticSeries(state) {
            return state.default.series
        },
        /**
         * Retorna todas as séries/parcelas cadastradas pelo usuário         
         */
        getSeries(state) {
            return state.data.series
        },
        /**
         * Retorna a tabela de vendas selecionada pelo usuário
         */
        getSalesTable(state) {
            return state.data.salesTable
        },

        /**
         * Retorna o fluxo selecionado pelo usuário
         */
        getSalesTableFlow(state) {
            return state.data.salesTableFlow
        },

        /**
         * Retorna o cliente selecionado pelo usuário
         */
        getCustomer(state) {
            return state.data.customer
        },

        /**
         * Retorna o responsável da simulação
         */
        getSimulationOwner(state) {
            return state.data.owner
        },

        /**
         * Retorna o negócio do cliente selecionado pelo usuário
         */
        getCustomerDeal(state) {
            return state.data.deal
        },

        /**
         * Retorna os dados das observações referentes a simulação
         */
        getObservation(state) {
            return state.data.observation
        },

        /**
         * Retorna os dados da simulação atual de acordo com o state
         */
        getSimulation(state, getters) {
            const rate = state.data.salesTable.monthly_rate || 0
            const user = JSON.parse(storage.get('user_logged'))
            const yearlyRate = salesTable.methods.facConvertInterest(rate, 12).toFixed(4)
            return {
                id: state.data.id,
                sales_model: state.data.salesTable.sales_model,
                deal_id: state.data.deal ? state.data.deal.id : null,
                default_data: JSON.stringify(state.default),
                flow_id: state.data.salesTableFlow ? state.data.salesTableFlow.id : null,
                owner_user_id: user ? user.id : null,
                sales_table_id: state.data.salesTable.id || null,
                sales_table : state.data.salesTable || [],
                unity_id: state.data.unity ? state.data.unity.id : null,
                data: {
                    customer: { ...state.data.customer },
                    deal: { ...state.data.deal },
                    tablesales: { ...state.data.salesTable },
                    unity: { ...state.data.unity }
                },
                installments: [...state.data.series],
                monthly_rate: rate,
                yearly_rate: yearlyRate,
                observation: state.data.observation,
                property_value: state.data.unity_cost,
                proposal_value: getters.getProposedValue,
                discount_saved: state.data.salesTable.discount ? state.data.salesTable.discount : 0,
                proposal_value_total: getters.getProposedFutureValue,
                simulator_default: state.data.default,
                type_table: state.data.salesTable.type || '',
                created_at: state.data.created_at
            }
        },

        /**
         * Retorna a quantidade da maior série mensal pertencente ao fluxo limitante
         * da tabela de vendas, caso exista
         */
        getQuantityLimit(state) {
            let limit = 0
            /** Verifica se existe uma tabela de vendas selecionada */
            if (!state.data.salesTable.id) return limit = -1

            /** Verifica se a tabela de vendas possui um fluxo limitante */
            let flow = state.data.salesTable?.sales_table_flow?.find(flow => flow.limit_flow)
            if (!flow) return limit

            /** Cria um array com as quantidades de todas as parcelas/séries mensais do fluxo */
            let quantities = Object.values(flow.sales_table_item)
                .filter(item => item.parcel_type === 'monthly')
                .map(item => item.quota)
            /** Pega a maior quantidade dentre as parcelas mensais */
            limit = quantities && quantities.length > 0 ? Math.max(...quantities) : 0

            return limit
        },

        /**
         * Retorna a quantidade da maior série/parcela do tipo entrada pertencente ao
         * fluxo limitante da tabela de vendas, caso exista
         */
        getSignalQuantityLimit(state) {
            let limit = 0

            /** Verifica se a tabela de vendas possui um fluxo limitante */
            let flow = state.data.salesTable?.sales_table_flow?.find(flow => flow.limit_flow)
            if (!flow) return limit

            /**
             * Cria um array com as quantidades de todas as parcelas/séries do tipo entrada
             * do fluxo
             */
            let quantities = Object.values(flow.sales_table_item)
                .filter(item => item.parcel_type === 'signal')
                .map(item => item.quota)
            /** Pega a maior quantidade dentre as parcelas mensais */
            limit = quantities && quantities.length > 0 ? Math.max(...quantities) : 0
            return limit
        },

        /**
         * Retorna a propriedade allow_fill do negócio/cliente selecionado para a simulação,
         * de modo a determinar se uma simulação poder ser salva para este cliente ou não
         */
        getAllowFill(state) {
            return state.data.deal && state.data.deal.allow_fill
                ? parseInt(state.data.deal.allow_fill)
                : 0
        },

        /**
         * Retorna um array com os tipos de parcelas/séries permitidos na etapa 2
         * do empreendimento
         */
        getAllowedSeriesTypes(state) {
            let types = []
            const product = state.data.product
            if (product &&
                product.availability &&
                product.availability.infos &&
                product.availability.infos.sales_table &&
                product.availability.infos.sales_table.parcel_type)
                /**
                 * Usa-se a função values pois o array de tipos de parcela pode vir com os indexes
                 * faltando (1,3,4,6,...), assim não é possível utilizar o spread operator para não pegar
                 * referência
                 */
                types = Object.values(product.availability.infos.sales_table.parcel_type)

            return types
        },

        /**
         * Retorna a as configurações da simulação definidas na etapa 2
         */
        getSimulationConfig(state) {
            const product = state.data.product
            return product &&
                product.availability &&
                product.availability.infos &&
                product.availability.infos.simulation
        },

        /**
         * Retorna a data de entrega do empreendimento
         */
        getDateDelivery(state) {
            const product = state.data.product
            const dateDelivery = product &&
                product.availability &&
                product.availability.infos &&
                product.availability.infos.sales_table &&
                product.availability.infos.sales_table.date_delivery

            /** Verifica se existe uma data de entrega e se esta é posterior ao dia atual */
            return dateDelivery && moment(dateDelivery, 'YYYY-MM-DD').isAfter(moment(), 'day')
                ? dateDelivery
                : null
        },

        /**
         * Calcula o valor da unidade com o disconto, caso haja
         */
        getUnityValueWithDiscount(state) {
            let value = state.data.unity_cost
            if (state.data.discount_saved > 0) {
                value = simulator.methods.facValueDiscount(value, state.data.discount_saved, 1)
            } else {
                if (state.data.salesTable.discount) value = simulator.methods.facValueDiscount(value, state.data.salesTable.discount, 1)
            }

            return value
        },

        /**
         * Calcula o valor fixo de acordo com as parcelas/séries do tipo Valor Fixo e
         * parcelas/séries referentes aos campos customizados configurados como desconto
         * na unidade
         */
        getFixedValue(state) {
            let value = 0
            for (const item of state.data.series) {
                if (item.type_installment === 'fixed_value' || item.consider === 'unity-discount')
                    value += parseFloat(item.first_value) * item.quantity || 0
            }

            return value
        },

        /**
         * Calcula o valor da proposta de acordo com os dados das parcelas/séries cadastradas
         */
        getProposedValue(state, getters) {
            let total = 0;
          
            let unityValue = getters.getUnityValueWithDiscount;
          
            const isHorizontal = getters.getSalesTable.simulationType === 'horizontal';
            const isVertical = getters.getSalesTable.simulationType === 'vertical';
          
            if (isHorizontal) {
              unityValue -= getters.getFixedValue;
            }
          
            for (const item of state.data.series) {
              const quantity = item.quantity || 1;
              const percent = parseFloat(item.percent || 0);
              const firstValue = parseFloat(item.first_value || 0);
          
              if (item.consider === 'unity-discount') {
                total += firstValue * quantity;
                continue;
              }
          
              if (item.type_installment === 'fixed_value' && isHorizontal) {
                total += firstValue * quantity;
              } else {
                if (percent === 0 && firstValue > 0 && isVertical) {
                  total += firstValue * quantity;
                } else {
                  total += ((percent * unityValue) / 100) || 0;
                }
              }
            }
          
            return total.toFixed(2);
          },

        /**
         * 
         * @param {*} state  
         * @param {*} getters 
         * @returns Objeto com o valor percentual e se é excedente ou faltante
         */
        getProposedPercent(state, getters) {
            //Tratar separadamente pra vertical e horizontal
            //Horizontal tem que desconsiderar o valor fixo, no vertical, considera o valor inteiro da unidade
            let unityValue = getters.getUnityValueWithDiscount
            if(getters.getSalesTable.simulationType ==  'horizontal') unityValue -= getters.getFixedValue
            let total = getters.getProposedValue - unityValue
            return {percent: total / getters.getUnityValueWithDiscount * 100, missing: total < 0, value: total * -1}
        },
        // /**
        //  * Retorna saldo pré e pós-chaves
        //  */
        // getPreKeysValue(state, getters) {
        //     let series = [...state.data.series]
        //     let preKeys = 0
        //     let postKeys = 0
        //     let deliveryDate = moment('2027-04-09')
        //     series.forEach(item => {
        //         if(moment(item.first_date).isSameOrBefore(deliveryDate)){
        //             preKeys += parseFloat(item.first_value) * item.quantity
        //         }
        //         if(moment(item.first_date).isSameOrAfter(deliveryDate)){
        //             postKeys += parseFloat(item.first_value) * item.quantity
        //         }
        //      })
        //     console.log(deliveryDate, preKeys, postKeys)
        // },

        /**
         * Calcula o valor futuro da proposta de acordo com os dados das parcelas/séries cadastradas
         */
        getProposedFutureValue(state) {
            let total = 0
            for (const item of state.data.series) {
                total += parseFloat(item.first_value) * item.quantity
            }

            return total.toFixed(2)
        },

        /**
         * Retorna os dados referentes a simulação padrão salva
         */
        getDefaultSimulation(state) {
            return state.default
        },

        /**
         * Calcula o valor faltante na simulação para chegar ao valor da unidade
         */
        getMissingValue(state, getters) {
            let missing = 0
            if (getters.getSalesTable.simulationType == 'vertical') {
                const tableVPL = Object.values(state.default.series)
                .map(series => {
                    if (series.installments && series.installments.length > 0) {
                        return series.installments.reduce(
                            (sum, installment) => sum + parseFloat(installment.pv || 0),
                            0
                        );
                    } else if (series.type_installment === 'fixed_value') {
                        return parseFloat(series.first_value || 0);
                    } else {
                        return 0;
                    }
                })
                .reduce((sum, value) => sum + value, 0);
            
                const proposalVPL = state.data.series
                    .map(item => {
                        if (item.installments && item.installments.length > 0) {
                            return item.installments.reduce(
                                (sum, installment) => sum + parseFloat(installment.pv || 0),
                                0
                            );
                        } else if (item.type_installment === 'fixed_value' ) {
                            return parseFloat(item.first_value || 0);
                        } else {
                            return 0;
                        }
                    })
                    .reduce((sum, value) => sum + value, 0);
                    
                missing = tableVPL.toFixed(6) - proposalVPL.toFixed(6)

            } else {
                missing = getters.getUnityValueWithDiscount - getters.getProposedValue
                missing = Math.round(missing * 100) / 100
            }
            return missing > 0 ? missing : 0
        },

        /**
         * Calcula o valor base da metragem quando a unidade possui área
         */
        getBaseCost(state) {
            let area = state.data.unity && state.data.unity.area || 0
            let unityCost = state.data.unity_cost
            // Formata a metragem e valor da unidade
            if (typeof area === 'string' && area.includes(',')) area = area.replace('.', '').replace(',', '.')
            if (typeof unityCost === 'string' && unityCost.includes(',')) unityCost = unityCost.replace('.', '').replace(',', '.')
        
            const baseCost = area ? parseFloat(unityCost) / parseFloat(area) : 0
            return baseCost ? baseCost.toFixed(2) : 0
        },
        
        /**
         * Calcula o valor da metragem de acordo com o valor da proposta calculado
         */
        getProposedBaseCost(state, getters) {
            let area = state.data.unity && state.data.unity.area || 0
            if (typeof area === 'string' && area.includes(',')) area = area.replace('.', '').replace(',', '.')
        
            const proposedBaseCost = area ? parseFloat(getters.getProposedValue) / parseFloat(area) : 0
            return proposedBaseCost ? proposedBaseCost.toFixed(2) : 0
        },

        /**
         * Calcula o valor da metragem de acordo com o valor futuro da proposta calculado
         */
        getProposedFutureBaseCost(state, getters) {
            const area = state.data.unity && state.data.unity.area ? parseFloat(state.data.unity.area) : 0
            const proposedFutureBaseCost = area ? getters.getProposedFutureValue / area : 0

            return proposedFutureBaseCost ? proposedFutureBaseCost.toFixed(2) : 0
        },

        /**
         * Verifica se há alguma proposta para um lançamento e retorna juntamente com o ID
         * do negócio
         */
		getHasProductInLaunchday(state) {
			return {
				hasLaunch: state.data.hasProductInLaunchday,
				idDeal: state.data.deal?.id
			}
		},
        /**
         * Retorna o tipo da tabela de vendas. Horizontal || Vertical
         */
        getSimulationType(state, getters){
            return getters.getSalesTable?.simulationType ||  state.data?.salesTable?.simulationType  || state.data?.product?.availability?.sales_table[0]?.simulationType || '';
        },
        /**
         * Retorna o valor da proposta em caso de simulador vertical pra salvar a simulação
         * @param state 
         * @returns 
         */
        getVerticalValue(state){
            return state.data.series
            .map(item => {
                return item.installments?.reduce((sum, installment) => sum + parseFloat(installment.pv || 0), 0)
            })
            .reduce((sum, item) => sum + item, 0)
        },
        getStageAllowFill(state) {
            return state.stageAllowFill;
        },
        /**
         * Verifica se existe alguma série/parcela com reajuste definido
         * @param state 
         * @returns boolean
         */
        getInstallmentsTypeReadjust(state) {
            let hasDefaultConfigs = false
            if(state.data.salesTable.default_config){
                hasDefaultConfigs = state.data.series.some(serie => 
                    JSON.parse(state.data.salesTable.default_config)?.readjust[serie.type_installment]
                )
            }
            
            let hasSeriesConfigs = state.data.series.some(serie => serie.type_readjust)
            return hasDefaultConfigs || hasSeriesConfigs
        },
        /**
         * @param {*} state 
         * @returns Configurações de reajuste padrão
         */
        getSeriesDefaultConfig(state){
            return state.data.salesTable.default_config
        }
    },

    mutations: {
        SET_PRODUCTS(state, payload){
            state.data.products = payload;
        },
        SET_PRODUCT(state, payload) {
            state.data.product = payload
        },

        SET_UNITY(state, payload) {
            state.data.unity = payload
        },

        SET_MONTHLY_RATE(state, payload) {
            state.data.salesTable.monthly_rate = payload
        },

        SET_UNIT_COST(state, payload) {
            state.data.unity_cost = payload
        },
        
        SET_CUSTOM_UNIT_COST(state, payload) {
            state.data.custom_unity_cost = payload
        },

        SET_SALES_TABLE(state, payload) {
            if (!payload) {
                Vue.set(state.data, 'salesTable', {});
            }
        
            Vue.set(state.data, 'salesTable', {
                sales_model: payload?.sales_model || null,
                id: payload?.id || null,
                monthly_rate: payload?.monthly_rate || null,
                discount: payload?.discount || null,
                type: payload?.type || null,
                sales_table_flow: payload?.sales_table_flow ? [...payload.sales_table_flow] : [],
                sales_simulator_rules: payload?.sales_simulator_rules ? [...payload.sales_simulator_rules] : [],
                sales_commission_rules: payload?.sales_commission_rules ? [...payload.sales_commission_rules] : [],
                duedates: payload?.duedates ? [...payload.duedates] : [],
                observations: payload?.observations || null,
                data: payload?.data || null,
                simulationType: payload?.simulationType || null,
                default_config: payload?.default_config || null,
            });
        
            Vue.set(state, 'extendableTypes', payload?.data &&
                typeof payload.data.allowExtend !== 'undefined' &&
                !payload.data.allowExtend
                    ? []
                    : [...extendableTypes]
            );
        },
        CLEAR_SALES_TABLE(state) {            
            state.data.salesTable = []
        },
        CLEAR_SERIES(state) {            
            Vue.set(state.data.series, 'series', []);
        },

        SET_DISCOUNT(state, payload) {
            state.data.discount_saved = payload.discount
        },

        SET_CUSTOMER(state, payload) {
            state.data.customer = payload ? { ...payload } : null
        },

        SET_CUSTOMER_DEAL(state, payload) {
            state.data.deal = payload ? { ...payload } : null
        },

        SET_SIMULATION_OWNER(state, payload) {
            state.data.owner = payload ? { ...payload } : null
        },

        SET_SALES_TABLE_FLOW(state, payload) {
            state.data.salesTableFlow = payload ? {...payload} : null
        },

        SET_OBSERVATION(state, payload) {
            state.data.observation = payload
        },

        /**
         * Seta os dados da unidade e produto coletados dos detalhes de uma unidade
         * ou da interna de um negócio
         */
		CHECK_UNITY_SIMULATION(state, payload) {
			state.data.product = payload.product || { availability: {} }
			state.data.unity = payload.unity || null
			state.data.customer = payload.customer || null
			state.data.owner = payload.customer ? payload.customer.owners : null
			state.data.deal = payload.deal || null
			state.data.unity_cost = payload.unity ? payload.unity.cost : 0
			state.data.salesTable.id = payload.sales_table_id || null
			state.data.hasProductInLaunchday = payload.has_product_in_launchday ? payload.has_product_in_launchday : false
			/**
             * Caso o empreendimento não possua tabelas de vendas disponíveis, utiliza-se
             * a taxa de juros configurada na etapa 2
             */
            if (payload.product &&
                payload.product.availability &&
                (!payload.product.availability.sales_table ||
                    !Object.values(payload.product.availability.sales_table).length)) {
                state.data.salesTable.monthly_rate = payload.product.availability.infos &&
                    payload.product.availability.infos.sales_table &&
                    payload.product.availability.infos.sales_table.monthly_rate ||
                    null
            }
            state.data.salesTableFlow = payload.sales_table_flow_id ? { id: payload.sales_table_flow_id } : null
        },

        /**
         * Carrega os dados da simulação já cadastrada no state
         */
        SET_SIMULATION(state, payload){
            try {
                state.data.id = payload.id
                state.data.data = payload.data
                state.data.observation = payload.observation
                state.data.default = payload.simulator_default
                state.data.discount_saved = payload.discount_saved
                state.data.created_at = payload.created_at
                /** Seta os dados do produto no state */
                state.data.product = payload.deal.product || payload.deal.product_without_global_scopes || payload.data.deal.product 
                /** Seta os dados da unidade no state */
                state.data.unity_cost = payload.property_value
                state.data.unity = payload.unity || payload.data.unity || { cost: payload.property_value, area: null }
                /** Seta os dados do negócio/cliente e responsável */
                state.data.deal = payload.deal
                state.data.customer = payload.deal.customer
                state.data.owner = payload.deal.owners
                /** Seta os dados das séries/parcelas da simulação */
                state.data.series = payload.installments
                state.data.installments = payload.installments
                /**
                 * Seta os dados necessários da simulação padrão utilizada
                 * Obs: Podem existir mais dados que o necessário
                 */
                if (payload.default_data && helpers.methods.isJSON(payload.default_data)) {
                    const defaultData = JSON.parse(payload.default_data)
                    state.default.presentValue = defaultData.presentValue || 0
                    state.default.futureValue = defaultData.futureValue || 0
                    state.default.baseCost = defaultData.baseCost || 0
                    state.default.series = defaultData.series || []
                }

                /** Seta os dados da tabela de vendas no state */
                if (payload.sales_table && payload.sales_table.id) {
                    state.data.salesTable.id = payload.sales_table.id
                    state.data.salesTable.observations = payload.sales_table.observations
                    state.data.salesTable.monthly_rate = payload.monthly_rate
                    state.data.salesTable.discount = payload.sales_table.discount
                    state.data.salesTable.type = payload.type_table
                    state.data.salesTable.simulationType = payload.sales_table.simulationType
                    state.data.salesTable.sales_table_flow = payload.sales_table.sales_table_flow
                        ? [...payload.sales_table.sales_table_flow]
                        : []
                    state.data.salesTable.sales_simulator_rules = payload.sales_table.sales_simulator_rules
                        ? [...payload.sales_table.sales_simulator_rules]
                        : []
                    state.data.salesTable.sales_commission_rules = payload.sales_table.sales_commission_rules
                        ? [...payload.sales_table.sales_commission_rules]
                        : []
                    state.data.salesTable.duedates = payload.sales_table.duedates
                        ? [...payload.sales_table.duedates]
                        : []
                    /** Seta os dados do fluxo no state */
                    if (payload.flow_id) {
                        const flow = state.data.salesTable.sales_table_flow.find(flow => flow.id === payload.flow_id)
                        state.data.salesTableFlow = flow || null
                    }
                    state.data.salesTable.data = payload.sales_table.data || null
                    /**
                     * Verfica se o usuário possui a configuração para permitir a prorrogação
                     * e antecipação de parcelas. Se ele não possuir tal regra, por padrão
                     * permite a prorrogação e antecipação.
                     */
                    state.extendableTypes = payload.sales_table.data &&
                        typeof payload.sales_table.data.allowExtend !== 'undefined' &&
                        !payload.sales_table.data.allowExtend
                            ? []
                            : [...extendableTypes]

                /** Verifica se a tabela de vendas da simulação está vencida ou foi removida */
                } else if (payload.sales_table_id) {
                    /**
                     * Caso o empreendimento não possua tabelas de vendas disponíveis, utiliza-se
                     * a taxa de juros configurada na etapa 2
                     */
                    if (state.data.product &&
                        state.data.product.availability &&
                        (!state.data.product.availability.sales_table ||
                            !Object.values(state.data.product.availability.sales_table).length)) {
                        state.data.salesTable.monthly_rate = state.data.product.availability.infos &&
                            state.data.product.availability.infos.sales_table &&
                            state.data.product.availability.infos.sales_table.monthly_rate ||
                            null
                    }
                    state.extendableTypes = [...extendableTypes]

                    throw new Error('A tabela de vendas vinculada a esta simulação foi removida ou está expirada. Por favor, fazer nova simulação.')
                }

            } catch (error) {
                Vue.prototype.$toastr.e(error.message)
                router.push('/vendas/simulacao')
            }
        },

        /**
         * Reseta os dados do state referentes a simulação e a simulação padrão
         */
        RESET_SIMULATION_DATA(state) {
            state.data = { ...simulationData() }
            state.default = { ...defaultSimulationData() }
        },

        /**
         * Salva os dados da simulação padrão definida pelo fluxo selecionado
         */
        SAVE_DEFAULT_SIMULATION(state, getters) {
            /**
             * Utiliza-se o JSON.parse juntamente com o JSON.stringify como um workaround para
             * copiar objetos/arrays com profundidades desconhecidas e não pegar referência do
             * objeto original
             */
            state.default.series = JSON.parse(JSON.stringify(state.data.series))
            state.default.presentValue = getters.getSalesTable.simulationType == 'vertical' ? getters.getVerticalValue : getters.getProposedValue
            state.default.futureValue = getters.getProposedFutureValue
            state.default.baseCost = getters.getProposedBaseCost
            /** Seta a simulação como padrão */
            state.data.default = 1
        },

        /**
         * Gera as séries/parcelas da simulação de acordo com o fluxo selecionado
         */
        GENERATE_SIMULATION_SERIES(state) {
            const flow = state.data.salesTableFlow
            let series = []
            if (flow && flow.id && flow.sales_table_item) {
                series = flow.sales_table_item.map(item => {
                    return generateSeries(item)
                })
            }
            state.data.series = series
        },

        /**
         * Adiciona uma série/parcela na simulação
         */
        ADD_SERIES(state) {
            const series = [...state.data.series]
            series.push({ ...seriesDefaultData() })
            state.data.series = series
        },

        /**
         * Remove uma série/parcela da simulação de acordo com o index desta
         */
        REMOVE_SERIES(state, payload) {
            const series = [...state.data.series]
            series.splice(payload, 1)
            Vue.set(state.data, 'series', series)
        },
        REMOVE_ALL_SERIES(state) {
            state.data.series = [];
        },


        /**
         * Calcula os valores iniciais do first_value e last_value das parcelas/séries de
         * acordo com os percentuais definidos na tabela de vendas
         */
        CALC_SERIES_INITIAL_VALUES(state, { commit, getters }) {
            let series = [...state.data.series]
            for(let [index, parcel] of series.entries()) {
                if (parcel.type_installment === 'fixed_value' && getters.getSalesTable.simulationType === 'horizontal' || parcel.consider === 'unity-discount'){
                    continue
                } 

                /** Reseta a data da série/parcela para o caso de esta ter sido prorrogada */
                series[index].first_date = series[index].first_date_default
                if (parcel.consider === 'payment-proposal') {
                    commit('CALC_SERIES_PERCENT', { getters, payload: index })

                } else {
                    commit('CALC_SERIES_VALUE', { getters, payload: index})
                }
            }
            state.data.series = series
        },

        /**
         * Calcula o valor da parcela/série de acordo com a porcentagem digitada.
         * Obs.: Como parcelas do tipo valor fixo ou desconto na unidade não possuem percentual
         * não é possível calcular o valor destas, assim, tendo que ser definido pelo usuário.
         */
        CALC_SERIES_VALUE(state, { getters, payload }) {
            if (typeof payload !== 'undefined' &&
                state.data.series[payload] &&
                state.data.series[payload].type_installment !== 'fixed_value' &&
                state.data.series[payload].consider !== 'unity-discount') {
                /** Como houve um recálculo da parcela, a simulação não é padrão */
                state.data.default = 0
                let parcel = { ...state.data.series[payload] }
                let unityValue = getters.getUnityValueWithDiscount
                if (getters.getSalesTable.simulationType == 'horizontal')  unityValue = getters.getUnityValueWithDiscount - getters.getFixedValue

                const parcelType = simulator.methods.facGetFrequency(parcel.type_installment)
                let salesTableType = getters.getSalesTable.simulationType == 'vertical'
                    ? 'vertical'
                    : state.data?.salesTable?.type
                let rate = parcel.interest_rate === 'with' || getters.getSalesTable.simulationType === 'vertical'
                    ? parseFloat(state.data.salesTable.monthly_rate)
                    : 0
                let offset = 0
                let vplOffset = 0
                let dayOffset = 0

                /** Caso a série/parcela não seja de um tipo permitido, não realiza a prorrogação */
                if ( getters.getSalesTable.simulationType == 'vertical') {
                    /**
                     * Calcula a diferença em meses entre a data selecionada e a data atual
                     * da simulação
                     */
                    offset = simulator.methods.facCalculateDiffDateVertical(
                        parcel.first_date,
                        parcel.first_date_default,
                        parcel.type_installment
                    )
                    /**
                     * Diff baseado na taxa de juros diária.
                     */
                    if(state.data.salesTable.data.daily_rate){
                        vplOffset = simulator.methods.facCalculateDiffDateVertical(
                            parcel.first_date,
                            moment().format('YYYY-MM-DD'),
                            parcel.type_installment
                        )
                        dayOffset = simulator.methods.facCalculateDiffDateDay(
                            parcel.first_date,
                            moment().format('YYYY-MM-DD'),
                            parcel.type_installment, 
                            vplOffset
                        )
                    } else {
                        vplOffset = simulator.methods.facCalculateDiffDate(
                            parcel.first_date,
                            moment().format('YYYY-MM-DD'),
                            parcel.type_installment
                        )
                    }

                

                } else if (state.extendableTypes.includes(parcel.type_installment) && rate) {
                    /**
                     * Calcula a diferença em meses entre a data selecionada e a data padrão (do fluxo)
                     * da série
                     */
                    offset = simulator.methods.facCalculateDiffDate(
                        parcel.first_date,
                        parcel.first_date_default,
                        parcel.type_installment
                    )
                }

                let installments = simulator.methods.facInstallmentPrice(
                    unityValue,
                    salesTableType,
                    parcel.percent / 100,
                    rate,
                    parcelType.period,
                    parseInt(parcel.quantity) || 0,
                    0, // Envia o valor fixo como zero devido a um bug na função que calcula o valor da parcela sem juros
                    true,
                    offset,
                    vplOffset,
                    state.data?.salesTable?.data?.daily_rate,
                    dayOffset
                )
               
                if (installments?.length) {
                    Vue.set(
                        state.data.series[payload], 
                        'installments', 
                        [...installments]
                      );
                    
                      Vue.set(
                        state.data.series[payload], 
                        'first_value', 
                        parseFloat(installments[0].valueInstallments)
                      );
    
                      Vue.set(
                        state.data.series[payload], 
                        'last_value', 
                        parseFloat(installments[installments.length - 1].valueInstallments)
                      );
                }
            }
        },

        /**
         * Calcula o percentual da parcela/série de acordo com o valor informado
         */
        CALC_SERIES_PERCENT(state, { getters, payload }) {
            if (typeof payload !== 'undefined' && state.data.series[payload]) {
                /** Como houve um recálculo da parcela, a simulação não é padrão */
                state.data.default = 0
                let parcel = { ...state.data.series[payload] }
                /** Não recalcula o percentual de parcelas/séries do tipo Valor Fixo */
                if (parcel.type_installment !== 'fixed_value' &&  parcel.consider !== 'unity-discount') {
                    let unityValue = getters.getUnityValueWithDiscount
                    if (getters.getSalesTable.simulationType == 'horizontal')  unityValue = getters.getUnityValueWithDiscount - getters.getFixedValue
                    const parcelType = simulator.methods.facGetFrequency(parcel.type_installment)
                    let rate = parcel.interest_rate === 'with' || getters.getSalesTable.simulationType == 'vertical'
                        ? parseFloat(state.data.salesTable.monthly_rate)
                        : 0
                    let interest = simulator.methods.facConvertInterest(rate, parcelType.period)
                    let offset = 0

                    /** Caso a série/parcela não seja de um tipo permitido, não realiza a prorrogação */
                    if (state.extendableTypes.includes(parcel.type_installment) && rate) {
                        /**
                         * Calcula a diferença em meses entre a data selecionada e a data padrão (do fluxo)
                         * da série
                         */
                        offset =  getters.getSalesTable.simulationType == 'vertical'
                            ? 0
                            : simulator.methods.facCalculateDiffDate(
                                parcel.first_date,
                                parcel.first_date_default,
                                parcel.type_installment
                            )
                    }
                    let newPercent = simulator.methods.facPercentFromPvWithoutInterest(
                        unityValue,
                        parcel.first_value,
                        interest,
                        parseInt(parcel.quantity) || 0,
                        getters.getSalesTable.simulationType ,
                        offset / parcelType.period
                    )
                    state.data.series[payload].percent = newPercent

                } else {
                    state.data.series[payload].percent = 0
                }
            }
        },

        /**
         * Calcula todos os valores referentes aos itens de uma série/parcela 
         */
        CALC_SERIES_INSTALLMENTS(state, { getters, payload }) {
            if (typeof payload !== 'undefined' && state.data.series[payload]) {
                /**
                 * Parcelas do tipo Valor Fixo ou que são campos customizados configurados como desconto
                 * na unidade não possuem percentual. Assim, não é possível calcular os installments e,
                 * portanto, não é possível setar o last_value
                 */
                if (state.data.series[payload].type_installment === 'fixed_value' ||
                    state.data.series[payload].consider === 'unity-discount') {
                    state.data.series[payload].last_value = state.data.series[payload].first_value
                    return
                }

                let parcel = { ...state.data.series[payload] }
                let unityValue = getters.getUnityValueWithDiscount
                if (getters.getSalesTable.simulationType == 'horizontal')  unityValue = getters.getUnityValueWithDiscount - getters.getFixedValue
                const parcelType = simulator.methods.facGetFrequency(parcel.type_installment)
                let salesTableType = getters.getSalesTable.simulationType == 'vertical'
                ? 'vertical'
                : state.data?.salesTable?.type
                let rate = parcel.interest_rate == 'with' || getters.getSalesTable.simulationType == 'vertical'
                    ? parseFloat(state.data.salesTable.monthly_rate)
                    : 0
                let offset = 0
                let vplOffset = 0
                let dayOffset = 0

                /** Caso a série/parcela não seja de um tipo permitido, não realiza a prorrogação */
                if (getters.getSalesTable.simulationType == 'vertical') {
                    /**
                     * Calcula a diferença em meses entre a data selecionada e a data atual
                     * da simulação
                     */
             
                    if(state.data.salesTable.data.daily_rate){
                        vplOffset = simulator.methods.facCalculateDiffDateVertical(
                            parcel.first_date,
                            moment().format('YYYY-MM-DD'),
                            parcel.type_installment
                        )
                        dayOffset = simulator.methods.facCalculateDiffDateDay(
                            parcel.first_date,
                            moment().format('YYYY-MM-DD'),
                            parcel.type_installment, 
                            vplOffset
                        )
                    } else {
                        vplOffset = simulator.methods.facCalculateDiffDate(
                            parcel.first_date,
                            moment().format('YYYY-MM-DD'),
                            parcel.type_installment
                        )
                    }
                

                } 
                if (state.extendableTypes.includes(parcel.type_installment) && rate && getters.getSalesTable.simulationType == 'horizontal') {
                    /**
                     * Calcula a diferença em meses entre a data selecionada e a data padrão (do fluxo)
                     * da série
                     */
                    offset = simulator.methods.facCalculateDiffDate(
                        parcel.first_date,
                        parcel.first_date_default,
                        parcel.type_installment
                    )

                }
                let installments = simulator.methods.facInstallmentPrice(
                    unityValue,
                    salesTableType,
                    parcel.percent / 100,
                    rate,
                    parcelType.period,
                    parseInt(parcel.quantity) || 0,
                    0, // Envia o valor fixo como zero devido a um bug na função que calcula o valor da parcela sem juros
                    true,
                    offset,
                    vplOffset,
                    state.data?.salesTable?.data?.daily_rate,
                    dayOffset
                )
                if (installments?.length) {
                    Vue.set(
                      state.data.series[payload], 
                      'installments', 
                      [...installments]
                    );
                  
                    const lastValue = parseFloat(installments[installments.length - 1].valueInstallments);

                    Vue.set(
                      state.data.series[payload], 
                      'last_value', 
                      lastValue
                    );
                }
                  

            }
        },

        /**
         * Calcula as datas de vencimento de todas os itens de uma série
         * 
         * @param {*} state 
         * @param {*} payload 
         */
        CALC_SERIES_DUE_DATE(state, { getters, payload }) {
            if (typeof payload !== 'undefined' &&
                state.data.series[payload] &&
                state.data.series[payload].first_date) {
                let parcel = state.data.series[payload]
                
                /**
                 * Parcelas do tipo Valor Fixo ou que são campos customizados configurados como desconto
                 * na unidade não possuem percentual. Assim, não é possível calcular os installments e,
                 * portanto, não é possível setar as datas
                 */
                if (parcel.type_installment === 'fixed_value' || parcel.consider === 'unity-discount') {
                    state.data.series[payload].last_date = parcel.first_date
                    return
                }

                let firstDate = moment(parcel.first_date, 'YYYY-MM-DD')

                /**
                 * Verifica se a data de entrega do empreendimento é anterior a data
                 * selecionada para a parcela/série
                 */
                if (getters.getDateDelivery &&
                    getters.getDateDelivery !== '0000-00-00' &&
                    moment(getters.getDateDelivery, 'YYYY-MM-DD').isBefore(firstDate)) {
                    firstDate = moment(getters.getDateDelivery, 'YYYY-MM-DD')
                }

                const day = firstDate.format('D')
                const month = firstDate.format('M')
                let quota = null
                let quotaBallon = null
                let firstDateBallon = null
                let parcelType = simulator.methods.facGetFrequency(parcel.type_installment)

                if (parcelType.balloon) {
                    quotaBallon = parcel.quantity
                    firstDateBallon = firstDate.format('YYYY-MM-DD')

                } else {
                    quota = parcel.quantity
                }

                const paymentMonth = simulator.methods.facReturnNumberMonthOrDate(parcel.first_date,
                    month,
                    day,
                    state.data.salesTable.duedates,
                    parcel.type_installment,
                    quota,
                    parcelType.balloon,
                    quotaBallon,
                    firstDateBallon,
                    parcel.type_installment,
                    parcelType.period
                )

                let duedates = []
                /** Verifica se é uma série/parcela mensal ou balão e recupera as datas de vencimento */
                if (paymentMonth.quota && paymentMonth.quota.length) {
                    duedates = [...paymentMonth.quota]

                } else if (paymentMonth.ballon && paymentMonth.ballon.length) {
                    duedates = [...paymentMonth.ballon]
                }

                /** Insere as datas de vencimento nos itens da série */
                if (duedates && duedates.length) {
                    state.data.series[payload].last_date = duedates[duedates.length - 1]
                    state.data.series[payload].installments = Object.values(state.data.series[payload].installments).map(installment => {
                        installment.duedate = duedates[installment.portion]
                        return installment
                    })
                    if (state.data.series[payload].first_date !== duedates[1]) state.data.series[payload].first_date = duedates[1]
                }
            }
        },

        /**
         * Gera as parcelas/séries referentes aos campos customizados de um negócio
         * (FGTS, Subsídio e Financiamento)
         * 
         * @param {*} state 
         */
        GENERATE_CUSTOM_FIELDS_SERIES(state, { getters, payload }) {
            if (payload && payload.payment) {
                for (const item of payload.payment) {
                    /** Caso o campo personalizado do cliente não possua valor, não gera a série/parcela */
                    if (!item.field.value) continue
                    
                    let formatted = item.field.value
                    if (formatted.includes(',')) formatted = formatted.replace('.', '').replace(',', '.')
                    let series = {
                        id: 'random-' + Math.floor((Math.random() * Date.now()) + 1),
                        type_installment: item.type,
                        quantity: 1,
                        percent: 0,
                        first_value: formatted || 0,
                        last_value: formatted || 0,
                        first_date: moment().format('YYYY-MM-DD'),
                        first_date_default: moment().format('YYYY-MM-DD'),
                        last_date: moment().format('YYYY-MM-DD'),
                        interest_rate: 'without',
                        installments: [],
                        consider: item.consider
                    }

                    /** Calcula o percentual para os campos cadastrados como pagamento na proposta */
                    if (item.consider === 'payment-proposal' && getters.getUnity && getters.getUnity.id) {
                        let unityValue = getters.getUnityValueWithDiscount
                        if (getters.getSalesTable.simulationType == 'horizontal')  unityValue = getters.getUnityValueWithDiscount - getters.getFixedValue
                        let parcelType = simulator.methods.facGetFrequency(series.type_installment)
                        let rate = 0
                        let interest = simulator.methods.facConvertInterest(rate, parcelType.period)
                        series.percent = simulator.methods.facPercentFromPvWithoutInterest(
                            unityValue,
                            series.first_value,
                            interest,
                            series.quantity,
                            state.data.salesTable.type
                        )
                    }
                    
                    /** Insere a nova série/parcela referente ao campo customizado na simulação */
                    state.data.series = [...state.data.series, series]
                }
            }
        },

        /**
         * Remove todas as séries/parcelas referentes a campos customizados
         */
        REMOVE_CUSTOM_FIELDS_SERIES(state) {
            state.data.series = Object.values(state.data.series)
                .filter(series => !['fgts_config', 'financing_config', 'allowance_config'].includes(series.type_installment))
        },

        /**
         * Redistribui o saldo faltante da simulação em uma parcela/série 
         */
        REDISTRIBUTE_MISSING_VALUE(state, { getters, payload }) {
            if (typeof payload === 'undefined' || !state.data.series[payload]) return
            /// Verifica se a série/parcela não possui quantidade
            if (!state.data.series[payload].quantity) state.data.series[payload].quantity = 1

            const missingValue = getters.getMissingValue

            // Verifica se a simulação é do tipo vertical
            if (getters.getSalesTable.simulationType == 'vertical') {
                const parcel = { ...state.data.series[payload] }
                const parcelType = simulator.methods.facGetFrequency(parcel.type_installment)
                const seriesVPL = parcel.installments && parcel.installments.length > 0
                    ? parcel.installments.reduce(
                        (sum, installment) => sum + parseFloat(installment.pv || 0),
                        0
                        )
                    : parcel.type_installment === 'fixed_value'
                    ? parseFloat(parcel.first_value || 0)
                    : 0;

                const rate = state?.data?.salesTable?.monthly_rate
                let valueVPL = missingValue + seriesVPL
                let value = 0
                const interest = salesTable.methods.facConvertInterestVertical(rate, parcelType.period)
                    let vplOffset = 0;
                    let  dayOffset = 0;
                    if(state.data.salesTable.data.daily_rate){
                        vplOffset = simulator.methods.facCalculateDiffDateVertical(
                            parcel.first_date,
                            moment().format('YYYY-MM-DD'),
                            parcel.type_installment
                        )
                        dayOffset = simulator.methods.facCalculateDiffDateDay(
                            parcel.first_date,
                            moment().format('YYYY-MM-DD'),
                            parcel.type_installment, 
                            vplOffset
                        )
    
                    } else {
                        vplOffset = simulator.methods.facCalculateDiffDate(
                            parcel.first_date,
                            moment().format('YYYY-MM-DD'),
                            parcel.type_installment
                        )
                    }
               

                    valueVPL = simulator.methods.facGetFVVertical(valueVPL, interest, vplOffset / parcelType.period, dayOffset)

                    value = salesTable.methods.facGetPGTO(interest, parcel.quantity, -valueVPL, 0, 1)


                    Vue.set(
                        state.data.series[payload], 
                        'first_value', 
                        value
                      );
                    
  
                      Vue.set(
                        state.data.series[payload], 
                        'last_value', 
                        value
                      );

            } else {
                /** Caso seja uma parcela do tipo Valor Fixo */
                if (state.data.series[payload].type_installment === 'fixed_value') {
                    state.data.series[payload].first_value = missingValue
                    state.data.series[payload].last_value = missingValue

                } else {
                    const unityCost = getters.getUnityValueWithDiscount - getters.getFixedValue
                    const missingPercent = (missingValue / unityCost) * 100
                    state.data.series[payload].percent = parseFloat(state.data.series[payload].percent) + missingPercent
                }
            }
        },

        /**
         * Verifica se a simulação é padrão ou não, de acordo com os dados
         * salvos do fluxo selecionado
         */
        CHECK_DEFAULT_SIMULATION(state) {
            /** Caso seja uma simulação livre ou a tabela de vendas não tenha fluxos, a simulação sempre é padrão */
            if (!state.data.salesTable.id || !state.data.salesTable.sales_table_flow.length) {
                state.data.default = 1
                return
            }

            let defaultSimulation = 1

            /**
             * Verifica se existem séries/parcelas cadastradas na simulação padrão e não na
             * simulação atual, ou vice-versa
             */
            const defaultTypes = [...state.default.series].map(parcel => parcel.type_installment)
            const simulationTypes = [...state.data.series].map(parcel => parcel.type_installment)
            const diffTypes = simulationTypes.filter(type => !defaultTypes.includes(type))
                .concat(defaultTypes.filter(type => !simulationTypes.includes(type)))
            if (diffTypes.length > 0) defaultSimulation = 0

            /**
             * Pega as propriedades a serem levadas em consideração na avaliação de se a
             * simulação é padrão
             */
            const getProperties = (parcel) => (({
                    type_installment,
                    quantity,
                    percent,
                    first_value,
                    first_date,
                    interest_rate
                }) => ({
                    type_installment,
                    quantity: parseInt(quantity),
                    percent: parseFloat(percent).toFixed(2),
                    first_value: parseFloat(first_value).toFixed(2),
                    first_date,
                    interest_rate
                }))(parcel)
            let defaultSeries = [...state.default.series].map(parcel => getProperties(parcel))
            let simulationSeries = [...state.data.series].map(parcel => getProperties(parcel))
            
            /**
             *  Para cada série/parcela da simulação, verifica se esta possui um equivalente
             *  na simulação padrão. Caso não exista, a simulação não é considerada padrão.
             */
            for (const parcel of simulationSeries) {
                let exists = false
                for (const [index, defaultParcel] of defaultSeries.entries()) {
                    if (defaultParcel.type_installment === parcel.type_installment) {
                        try {
                            /** Verifica se os objetos são iguais */
                            assert.deepStrictEqual(parcel, defaultParcel)
                            exists = true
                            /** Remove a série da simulação padrão, pois seu equivalente já foi encontrado */
                            defaultSeries.splice(index, 1)
                            break
                        } catch (e) {
                            exists = false
                        }
                    }
                }
                
                /** Se nenhum correspodente for encontrado na simulação padrão, então a simulação atual não é padrão */
                if (!exists) defaultSimulation = 0
            }

            /** Define se a simulação é padrão ou não */
            state.data.default = defaultSimulation
        },

        /**
         * Valida as regras de simulação definidas para a tabela de vendas selecionada
         */
        VALIDATE_SIMULATION_RULES(state, getters) {
            if(!state.data.salesTable?.id) return;
            for (const rule of state.data.salesTable.sales_simulator_rules) {
                let ruleAmount = parseFloat(rule.amount).toFixed(2)
                let ruleDate = null
                let unityValue = null
                let tolerance = false
                let toleranceValue = 0
                let percent = 0
                let message = ''
                let messageValue = 0

                /** Se a tolerância está desativada, não é necessário validar a regra */
                if (rule.tolerance_status === 0) {
                    continue

                /** Se existe tolerância */
                } else if (rule.tolerance_status === 2) {
                    tolerance = true
                    toleranceValue = parseFloat(rule.tolerance).toFixed(2)
                }

                /** Define o valor a aparecer na mensagem de erro */
                if (tolerance) {
                    messageValue = toleranceValue
                } else {
                    messageValue = ruleAmount
                }

                switch (rule.type) {
                    case 'present_value':
                        percent = getSpecificPercent([], state.data.series)
                        message = `Sua proposta está abaixo do valor permitido de ${messageValue}% do valor da unidade`
                        break
                
                    case 'entry':
                        /** Se não houver nenhuma parcela do tipo entrada, não valida a regra de entrada */
                        if (!state.data.series.find(parcel => parcel.type_installment === 'signal')) continue

                        percent = getSpecificPercent(['signal'], state.data.series)
                        message = `Suas parcelas de entrada estão abaixo do valor permitido de ${messageValue}% do valor da unidade`
                        break
                    
                    case 'balloon':
                        var types = [
                            'bimonthly',
                            'quarterly',
                            'four_monthly',
                            'semester',
                            'yearly'
                        ]
                        /** Se não houver nenhuma parcela do tipo balão, não valida a regra de balão */
                        if (!state.data.series.find(parcel => types.includes(parcel.type_installment))) continue

                        percent = getSpecificPercent(types, state.data.series)
                        message = `Suas parcelas do tipo balão estão abaixo do valor permitido de ${messageValue}% do valor da unidade`
                        break

                    case 'bank_financing':
                        /** Se não houver nenhuma parcela do tipo financiamento, não valida a regra de financiamento */
                        if (!state.data.series.find(parcel => parcel.type_installment === 'financing')) continue

                        unityValue = getters.getUnityValueWithDiscount - getters.getFixedValue
                        ruleDate = moment().add(rule.days, 'days')
                        percent = getSpecificPercent(['financing'],
                            state.data.series,
                            ruleDate,
                            unityValue
                        )
                        message = `Suas parcelas do tipo financiamento estão abaixo do valor permitido de ${messageValue}% do valor da unidade`
                        break

                    case 'captation':
                        unityValue = getters.getUnityValueWithDiscount - getters.getFixedValue
                        ruleDate = moment().add(rule.days, 'days')
                        percent = getSpecificPercent([],
                            state.data.series,
                            ruleDate,
                            unityValue
                        )
                        message = `Esta tabela de vendas exige uma captação de ${messageValue}% do valor da unidade em até ${rule.days} dias`
                        break
                }

                if ((tolerance && parseFloat(percent) < parseFloat(toleranceValue)) ||
                    (!tolerance && parseFloat(percent) < parseFloat(ruleAmount))) {
                    throw message
                }
            }
        },
        SET_STAGE_ALLOW_FILL(state, payload) {
            state.stageAllowFill = payload
        }
    },

    actions: {
        setProductsForSimulation({commit},payload){
        //Inserir no storage
        //simulations.products
        commit('SET_PRODUCTS', payload)
        },
        /**
         * Verifica se a simulação foi iniciada através da página de detalhes de uma unidade,
         * ou a partir da interna de um negócio
         */
		checkUnitySimulation({ commit, dispatch, state }, { payload, deal_id }) {
            return new Promise((resolve, reject) => {
                const storageKey = `guide_simulationUnity_${payload.id}`;
                const unitySimulation = JSON.parse(storage.getLegacy(storageKey));
        
                /** Verifica se existem dados de inicialização da simulação no localStorage */
                if (unitySimulation?.step2?.product_id) {
                    /** Busca os dados do produto vinculado ao negócio ou unidade */
                    product.getLimitedTableFlow(unitySimulation.step2.product_id, unitySimulation.step2.has_product_in_launchday ? unitySimulation.step2.deal.id : null)
                        .then(response => {
                            commit('CHECK_UNITY_SIMULATION', {
                                product: { ...response.data },
                                unity: unitySimulation.step2.unity,
                                customer: unitySimulation.step2.customer,
                                deal: unitySimulation.step2.deal,
                                sales_table_id: unitySimulation.step2.sales_table_id,
                                sales_table_flow_id: unitySimulation.step2.sales_table_flow_id,
                            });
                            /** Remove os dados de inicialização do localStorage */
                            storage.removeLegacy(storageKey);
                            /**Faz um reject pra caso esteja criando uma simulação do zero pelo modal de previa no espelho digital */
                            if (!parseFloat(unitySimulation?.step2?.unity?.cost)) {
                                reject();
                              }
                            /** Resolve se a unidade foi selecionada, caso contrário, rejeita */
                            if (unitySimulation.step2.unity) {
                                resolve();
                            } else {
                                reject();
                            }
                        })
                        .catch(error => {
                            if ( error?.data?.msg) Vue.prototype.$toastr.e(error.data.msg);
                            reject(error); 
                        });
                } else {
                    const interestUnity = JSON.parse(storage.getLegacy('guide_new_deal_unity'));
                     dispatch('setSimulation', deal_id).then(() => {  
                        //Usar essa condicional pra atualizar a unidade desse empreendimento novo
                        let unityCost;
                        let dealCost;
                        let productHasChanged = false;
                        if(!state.data?.deal?.product_id) reject({'noProduct':true})
                        if(!state.data?.data?.deal?.product?.id && !state?.data?.product?.id) {
                            reject({'noProduct':true})
                        }
                        if (!unitySimulation && !!(interestUnity?.data.newUnity?.id || interestUnity?.data?.formUnity?.id) && state?.data?.data?.deal?.product?.id && parseInt(state?.data?.data?.deal?.product?.id) != parseInt(state.data?.product?.id)) {
                            let newUnity = interestUnity?.data?.newUnity?.id ? interestUnity?.data.newUnity : interestUnity?.data?.formUnity 
                            unityCost = newUnity.cost;
                            dealCost = state.data?.deal?.cost;
                            if(interestUnity?.data?.formUnity?.availability_id ==  state.data?.product.availability_id) {
                                productHasChanged = false
                            } else {
                                productHasChanged = true
                            }
                            commit('SET_UNITY', newUnity);
                            commit('SET_UNIT_COST', newUnity.cost);
                        } 
                        if(state?.data?.data?.deal?.product?.id && parseInt(state?.data?.data?.deal?.product?.id) != parseInt(state.data?.product?.id)){
                            productHasChanged = true
                        }
                        reject({'simulation_id': state.data?.id, 'unity_cost':unityCost, 'deal_cost':dealCost, 'productHasChanged': productHasChanged , 'noProduct':false});
                    }).catch((error) => {
                        reject(error);
                    })
        
                    /** Mesmo que não exista produto e unidade, verifica se existem dados do negócio */
                    if (unitySimulation?.step2?.deal) {
                        commit('SET_CUSTOMER', unitySimulation.step2.customer);
                        commit('SET_CUSTOMER_DEAL', unitySimulation.step2.deal);
                        commit('SET_SIMULATION_OWNER',
                            unitySimulation.step2.customer
                                ? unitySimulation.step2.customer.owners
                                : null
                        );
        
                        /** Remove os dados de inicialização do localStorage */
                        reject();
                        storage.removeLegacy(storageKey);
                    }
                    storage.removeLegacy('guide_new_deal_unity');
                }
            });
        },
        

        /**
         * Seta no state o produto selecionado pelo usuário
         */
        setSimulationProduct({ commit, getters }, payload) {
            if (!getters.getProduct || (getters.getProduct && getters.getProduct.id !== payload.product.id)) {
                /** Limpa os dados da tabela de vendas do produto anterior */
                commit('SET_SALES_TABLE', {
                    id: null,
                    monthly_rate: null,
                    discount: 0,
                    type: 'price',
                    sales_table_flow: [],
                })
                commit('SET_PRODUCT', payload.product)
            }

            /**
             * Caso não exista uma tabela de vendas selecionada, altera-se a taxa
             * mensal para o valor digitado no modal de seleção do produto
             */
            if (!getters.getSalesTable.id) commit('SET_MONTHLY_RATE', payload.monthly_rate)
        },

        /**
         * Seta no state os dados referentes a unidade selecionada pelo usuário
         */
        setSimulationUnity({ commit, state }, payload) {
            /**
             * Verifica se a unidade do payload é um objeto (instância do banco) ou se é
             * apenas um nome fictício
             */
            if (typeof payload.unity === 'object') {
                if(parseInt(payload.unity?.cost) > 0) commit('SET_UNIT_COST', payload.unity?.cost)
                commit('SET_UNITY', payload.unity)

            } else {
                const unity = {
                    number: payload.unity,
                    cost: payload.unity_cost,
                    area: null
                }
                commit('SET_UNIT_COST', payload.unity_cost)
                commit('SET_UNITY', unity)
            }
        },
        /** Atualiza o valor customizado pra unidades sem custo */
        updateCustomValue({ commit }, value) {
            commit('SET_CUSTOM_UNIT_COST', value);
        },
        /**Insere o valor da unidade pro unity_cost */
        setUnityValue({commit}, payload){
            commit('SET_UNIT_COST', payload || 0)
        },
         /**
         * Insere no state o valor de unidades que não possuem valor cadastrado
         */
        /**
         * Seta no state a tabela de vendas selecionada pelo usuário
         */
        setSalesTable({ commit }, payload) {
            commit('SET_SALES_TABLE', payload)
        },
        clearSalesTable({ commit }) {
            commit('CLEAR_SALES_TABLE')
        },
        clearSeries({ commit }) {
            commit('CLEAR_SERIES')
        },
        /**
         seta o disconto para atualizar a tabela
         */
        setDiscount({ commit }, payload) {
            commit('SET_DISCOUNT', payload)
        },

        /**
         * Seta no state os dados do cliente selecionado pelo usuário 
         */
        setCustomer({ commit }, payload) {
            commit('SET_CUSTOMER', payload)
            /** Remove todas as séries/parcelas referentes a campos customizados do cliente/negócio anterior */
            commit('REMOVE_CUSTOM_FIELDS_SERIES')
        },

        /**
         * Gera as séries/parcelas referentes aos campos personalizados, caso existam,
         * do negócio vinculado a simulação
         */
        generateCustomFieldsSeries({ commit, getters }) {
            if (getters.getCustomerDeal && getters.getCustomerDeal.id) {
                deal.get(getters.getCustomerDeal.id, '/group/proposal')
                    .then(response => {
                        commit('GENERATE_CUSTOM_FIELDS_SERIES', { getters, payload: response.data })
                    })
                    .catch(error => {
                        if (error.data && error.data.msg) Vue.prototype.$toastr.e(error.data.msg)
                        console.log(error)
                    })
            }
        },

        /**
         * Seta no state os dados do negócio do cliente selecionado pelo usuário 
         */
        setCustomerDeal({ commit, dispatch }, payload) {
            commit('SET_CUSTOMER_DEAL', payload)
            if (payload && payload.id) dispatch('generateCustomFieldsSeries')
        },

        /**
         * Seta no state os dados do owner do negócio/cliente selecionado pelo usuário
         */
        setSimulationOwner({ commit }, payload) {
            commit('SET_SIMULATION_OWNER', payload)
        },

        /**
         * Seta no state todos os dados referentes a uma simulação que está sendo carregada
         */
        setSimulation({ commit, getters }, payload) {
            // eslint-disable-next-line no-async-promise-executor
            return new Promise(async (resolve, reject) => {
                try {
                    let response = await deal.get(payload, '/simulation')
                    commit('SET_SIMULATION', response.data)
                    /**
                     * Percorre as séries cadastradas, de modo a calcular as datas de vencimento
                     * e os itens de cada uma
                    */
                    getters.getSeries.map((series, index) => {
                        commit('CALC_SERIES_INSTALLMENTS', { getters, payload: index })
                        commit('CALC_SERIES_DUE_DATE', { getters, payload: index })
                    })
                    resolve()
                } catch (error) {
                    reject(error)
                }
            })
        },
        /**
         * Seta no state as observações referentes a simulação 
         */
        setObservation({ commit }, payload) {
            commit('SET_OBSERVATION', payload)
        },

        /**
         * Seta no state o fluxo selecionado pelo usuário
         */
        setSalesTableFlow({ commit }, payload) {
            commit('SET_SALES_TABLE_FLOW', payload)
        },

        /**
         * Gera no state as séries/parcelas a partir do fluxo selecionado
         */
        generateSimulationSeries({ commit, getters }) {
            commit('GENERATE_SIMULATION_SERIES')
            commit('CALC_SERIES_INITIAL_VALUES', { commit, getters })
            /** Percorre as séries cadastradas, de modo a calcular as datas de vencimento */
            getters.getSeries.map((series, index) => {
                commit('CALC_SERIES_DUE_DATE', { getters, payload: index })
            })
            /** Salva a simulação referente ao fluxo selecionado como padrão */
            commit('SAVE_DEFAULT_SIMULATION', getters)
        },

        /**
         * Calcula os valores iniciais das séries a partir de seus percentuais
         */
        calcSeriesInitialValues({ commit, getters }) {
            commit('CALC_SERIES_INITIAL_VALUES', { commit, getters })
            /** Percorre as séries cadastradas, de modo a calcular as datas de vencimento */
            getters.getSeries.map((series, index) => {
                commit('CALC_SERIES_DUE_DATE', { getters, payload: index })
            })
            /** Salva a simulação referente ao fluxo selecionado como padrão */
            commit('SAVE_DEFAULT_SIMULATION', getters)
        },

        /**
         * Adiciona no state uma série/parcela vazia para a simulação
         */
        addSimulationSeries({ commit, getters }) {
            commit('ADD_SERIES')
            /**
             * Pega o tamanho do array de séries/parcelas cadastradas de modo a obter o index da última
             * série/parcela cadastrada
             */
            const payload = getters.getSeries.length - 1
            commit('CALC_SERIES_INSTALLMENTS', { getters, payload })
            commit('CALC_SERIES_DUE_DATE', { getters, payload })
        },

        /**
         * Remove do state e da simulação uma série/parcela de acordo com seu index
         */
        removeSimulationSeries( { commit }, payload) {
            commit('REMOVE_SERIES', payload)
        },
        removeAllSimulationSeries({ commit }) {
            commit('REMOVE_ALL_SERIES')
        },

        /**
         * Calcula o valor de uma série/parcela de acordo com seu percentual
         */
        calcSeriesValue({ commit, getters }, payload) {
            commit('CALC_SERIES_VALUE', { getters, payload })
            commit('CALC_SERIES_DUE_DATE', { getters, payload })
        },

        /**
         * Calcula o percentual de uma série/parcela de acordo com seu valor
         */
        calcSeriesPercent({ commit, getters }, payload) {
            commit('CALC_SERIES_PERCENT', { getters, payload })
            commit('CALC_SERIES_INSTALLMENTS', { getters, payload })
            commit('CALC_SERIES_DUE_DATE', { getters, payload })
        },

        /**
         * Recebe o ID de uma série/parcela como parâmetro e distribui o saldo faltante
         * nesta 
         */
        redistributeMissingValue({ commit, getters, dispatch }, payload) {
            commit('REDISTRIBUTE_MISSING_VALUE', { getters, payload })
            if (getters.getSeries[payload]?.type_installment === 'fixed_value') {
                for (const index in getters.getSeries) {
                    if (index !== payload) dispatch('calcSeriesPercent', index)
                }

            } else if (getters.getSalesTable.simulationType == 'vertical') {
                commit('CALC_SERIES_PERCENT', { getters, payload })
                commit('CALC_SERIES_INSTALLMENTS', { getters, payload })

            } else {
                commit('CALC_SERIES_VALUE', { getters, payload })
            }
            commit('CALC_SERIES_DUE_DATE', { getters, payload })
        },

        /**
         * Salva a simulação atual. Caso não possua ID cria-se uma nova vinculando-a ao cliente.
         */
        saveSimulation({ commit, getters }) {
            // eslint-disable-next-line no-async-promise-executor
            return new Promise(async (resolve, reject) => {
                try {
                    /** Verifica se a simulação é padrão */
                    commit('CHECK_DEFAULT_SIMULATION')
                    /** Valida as regras de simulação */
                    commit('VALIDATE_SIMULATION_RULES', getters)

                    const simulationData = getters.getSimulation
                    /** Verifica se existe alguma parcela/série com valor null, zero ou negativo */
                    const notEmpty = simulationData.installments.filter(item => {
                        return !item.first_value || parseFloat(item.first_value) < 0
                    })
                    if (notEmpty.length) {
                        throw new Error('Não é possível salvar. Por favor, verifique se existem parcelas com valor menor ou igual a 0!')
                    }

                    let response = null
                    if (getters.getCustomerDeal && getters.getSimulationOwner) {
                        response = await simulationService.create('', simulationData)
                        /** Atualiza os dados do negócio de acordo com o valor da proposta */
                        let dealData = {
                            product_id: getters.getProduct.id,
                            unity_id: simulationData.unity_id,
                            cost: simulationData.proposal_value
                        }
                        const responseDeal = await deal.update(simulationData.deal_id, '', dealData)
                        /** Atualiza os dados do negócio do cliente no state */
                        commit('SET_CUSTOMER_DEAL', responseDeal.data.data)

                    } else {
                        /** Não possui dados suficientes para salvar a simulação */
                        throw new Error('Por favor selecione um cliente para a simulação.')
                    }

                    resolve(response)
                } catch (error) {
                    reject(error)
                }
            })
        },

        clearSimulation({ commit }) {
            commit('RESET_SIMULATION_DATA')
        },

        async fetchStageAllowFill({ commit, state, getters }) {

            if (state.stageAllowFill !== null) return; 
        
            const customerDeal = getters.getCustomerDeal;

            if (!customerDeal || !customerDeal.funnel_stage_id) return
        
            const response = await funnel.getStages(); 
            const stage = response.data.find(stage => stage.id === customerDeal.funnel_stage_id);
            let allowFill = stage?.allow_fill ?? 0;
        
            if (stage?.privacy === 'public') {
                allowFill = 1;
            }
            
            commit('SET_STAGE_ALLOW_FILL', parseInt(allowFill));
        }
    }
}